import { forwardRef } from 'react';
import {
  domainLabel,
  domainLabels,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import type { CaseMapState } from './coverage-geometry';
import styles from './case-coverage-map.module.scss';

// As the spec words the unconfirmed legend: "Missing: Taxes · DCA".
const domainList = (keys: readonly string[]) =>
  keys.map(domainLabel).join(' · ');

interface CoverageLegendProps {
  state: CaseMapState;
  /** Each assigned coverage scenario (Type Curve included), a footprint too large to send included. */
  overlaidCount: number;
  /** A domain is missing or archived: no green until it isn't. */
  blocked: boolean;
  /** The common region is drawn. */
  green: boolean;
  /** Red is left around the green. */
  red: boolean;
  /** A union or a cut-out failed. */
  coverageFailed: boolean;
  /** The package's valued slots are drawn. */
  valuedSlots: boolean;
  /** Places the TC team rejected are drawn, keyed with the slot grid's term for it. */
  rejectedSlots?: boolean;
  missing: readonly ScenarioDomainKey[];
  archived: readonly ScenarioDomainKey[];
  /** Domains whose scenario pins no area ('empty'). */
  noAreaDomains: readonly string[];
  /** Domains whose footprint was too large to send ('unavailable'). */
  tooLargeDomains: readonly string[];
  /** A Type Curve footprint is among the ones drawn. */
  typeCurveDrawn?: boolean;
}

/**
 * The coverage map's legend, over its bottom-left corner: what the red stack, the dashed frame and
 * the green mean, and why the green is missing when it is.
 */
export const CoverageLegend = forwardRef<HTMLUListElement, CoverageLegendProps>(
  (
    {
      state,
      overlaidCount,
      blocked,
      green,
      red,
      coverageFailed,
      valuedSlots,
      rejectedSlots = false,
      missing,
      archived,
      noAreaDomains,
      tooLargeDomains,
      typeCurveDrawn = false,
    },
    ref
  ) => {
    return (
      <ul ref={ref} className={styles.legend} aria-label="Coverage map legend">
        <li className={styles.legendItem} data-testid="coverage-stack">
          <span className={styles.swatchFootprint} />
          {overlaidCount === 1
            ? '1 scenario overlaid'
            : `${overlaidCount} scenarios overlaid`}
          : deeper red means more scenarios stacked
        </li>
        <li className={styles.legendItem}>
          <span className={styles.swatchUnion} />
          Dashed: the combined footprint
        </li>
        {blocked && (
          <li className={styles.legendItem} data-testid="coverage-unconfirmed">
            Unconfirmed: green shows once all eight domains are assigned and
            none is archived
          </li>
        )}
        {green && (
          <li className={styles.legendItem}>
            <span className={styles.swatchCommon} />
            Covered: every scenario reaches here
          </li>
        )}
        {typeCurveDrawn && (
          <li className={styles.legendItem} data-testid="coverage-type-curve">
            Green is where every coverage scenario reaches; PDPs are valued
            outside Type Curve areas too
          </li>
        )}
        {green && red && (
          <li className={styles.legendItem} data-testid="coverage-red">
            Red: at least one scenario doesn&apos;t reach here
          </li>
        )}
        {coverageFailed && (
          <li
            className={styles.legendItem}
            data-testid="coverage-not-covered-failed"
          >
            Coverage couldn&apos;t be computed for every area
          </li>
        )}
        {valuedSlots && (
          <li className={styles.legendItem}>
            <span className={styles.swatchValued} />
            Valued slot
            <span className={styles.swatchUnvalued} />
            Not valued (zoom in to see slots)
          </li>
        )}
        {rejectedSlots && (
          <li className={styles.legendItem} data-testid="coverage-rejected">
            <span className={styles.swatchRejected} />
            Unvalued in typecurve
          </li>
        )}
        {missing.length > 0 && (
          <li className={styles.legendItem} data-testid="coverage-missing">
            Missing: {domainList(missing)}
          </li>
        )}
        {archived.length > 0 && (
          <li className={styles.legendItem} data-testid="coverage-archived">
            Archived: {domainList(archived)}
          </li>
        )}
        {state === 'pending' && (
          <li className={styles.legendItem} data-testid="coverage-pending">
            Updating the common area...
          </li>
        )}
        {state === 'empty' && (
          <li className={styles.legendItem} data-testid="coverage-empty">
            {noAreaDomains.length > 0
              ? `No common area: ${domainLabels(noAreaDomains)} pins no area`
              : 'No common area: the scenarios share none'}
          </li>
        )}
        {state === 'unavailable' && (
          <li className={styles.legendItem} data-testid="coverage-unavailable">
            {tooLargeDomains.length > 0
              ? `Common area not drawn: ${domainLabels(
                  tooLargeDomains
                )} is too large to show`
              : "Common area not drawn: it couldn't be worked out here"}
          </li>
        )}
      </ul>
    );
  }
);
CoverageLegend.displayName = 'CoverageLegend';
